import { useInfiniteQuery } from '@tanstack/react-query'
import { useCrsfToken } from '~/hooks/useCrsfToken'
import { JsonRpcError } from '~/types'

const API_URL = '/team-tools/api/player/sessions_history'

export const useGameSessionHistory = ({
    user_id,
    cheatedOnly = false,
    killsMin = '',
    killsMax = '',
    // Their last N days, measured from their own most recent session.
    recentDays = 0,
    // The matches these two met in, for the "who they played" tab.
    opponentId = 0,
    opponentUsername = '',
    opponentKilledThem = false,
    // Only the matches overlapping this stretch of time (ISO datetimes).
    betweenFrom = '',
    betweenTo = '',
    enabled = true,
}) => {
    const csrf = useCrsfToken()
    const limit = 20
    const query = useInfiniteQuery<GameSessionHistory, JsonRpcError>({
        // `cheatedOnly` is part of the key, so toggling it refetches from page
        // zero instead of appending a filtered page onto the unfiltered ones
        // already cached.
        // Both bounds are part of the key for the same reason `cheatedOnly`
        // is: a changed filter must refetch from page zero rather than
        // append a differently-filtered page onto the ones already cached.
        queryKey: [
            API_URL,
            user_id,
            cheatedOnly,
            killsMin,
            killsMax,
            recentDays,
            opponentId,
            opponentUsername,
            opponentKilledThem,
            betweenFrom,
            betweenTo,
        ],
        queryFn: async ({ pageParam = 0 }) => {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrf,
                },
                body: JSON.stringify({
                    user_id,
                    page: pageParam,
                    limit,
                    cheated_only: cheatedOnly,
                    kills_min: killsMin,
                    kills_max: killsMax,
                    recent_days: recentDays || undefined,
                    opponent_id: opponentId || undefined,
                    opponent_username: opponentUsername || undefined,
                    opponent_killed_them: opponentKilledThem,
                    between_from: betweenFrom || undefined,
                    between_to: betweenTo || undefined,
                }),
            })
            const data = await res.json()
            return data
        },
        getNextPageParam: (lastPage) => {
            const current = lastPage.page
            const length = lastPage.data.length
            return length == limit ? current + 1 : undefined
        },
        retry: false,
        enabled,
    })
    const sessions = query.data?.pages.flatMap((page) => page.data)
    const count = query.data?.pages[0].count
    return { ...query, sessions, count }
}
